'use client'

import { useState } from 'react'

const TRANSPORT_COLORS = {
  '🚶': 'bg-blue-50 text-blue-700 border-blue-200',
  '🛵': 'bg-amber-50 text-amber-700 border-amber-200',
  '🚗': 'bg-orange-50 text-orange-700 border-orange-200',
  '🚌': 'bg-green-50 text-green-700 border-green-200',
  '🚲': 'bg-teal-50 text-teal-700 border-teal-200',
}

function DayBlock({ day }) {
  return (
    <div className="border-l-2 border-gray-200 pl-4 py-1">
      <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
        Day {day.day}
      </div>
      <ul className="space-y-2 mb-3">
        {day.activities.map((act, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5 w-5 h-5 flex-shrink-0 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center leading-none">
              {i + 1}
            </span>
            <div>
              <span className="font-medium text-gray-800">{act.name}</span>
              <span className="text-gray-500"> — {act.desc}</span>
            </div>
          </li>
        ))}
      </ul>
      {day.restaurant && (
        <div className="flex items-start gap-2 text-sm bg-orange-50 rounded-lg px-3 py-2">
          <span className="text-base leading-none mt-0.5">🍽</span>
          <div>
            <span className="font-medium text-gray-800">{day.restaurant.name}</span>
            <span className="text-gray-500 text-xs"> — {day.restaurant.note}</span>
          </div>
        </div>
      )}
    </div>
  )
}

function ItineraryAccordion({ plan }) {
  const [open, setOpen] = useState(false)
  const transportCls = TRANSPORT_COLORS[plan.transport] || 'bg-gray-50 text-gray-700 border-gray-200'

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <span className={`flex-shrink-0 text-sm font-semibold px-2.5 py-1 rounded-full border ${transportCls}`}>
          {plan.transport} {plan.transport_label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-800 text-sm">{plan.title}</div>
          <div className="text-xs text-gray-400 mt-0.5">{plan.duration}</div>
        </div>
        <svg
          className={`flex-shrink-0 w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-5 border-t border-gray-100 pt-4 bg-white">
          {plan.days.map((day) => (
            <DayBlock key={day.day} day={day} />
          ))}
        </div>
      )}
    </div>
  )
}

export function TravelPlans({ itineraries, cityName }) {
  if (!itineraries?.length) return null
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-700 mb-1">Travel Plans</h2>
      <p className="text-xs text-gray-400 mb-3">
        Suggested itineraries for {cityName} — tap a plan to see day-by-day details.
      </p>
      <div className="space-y-2">
        {itineraries.map((plan) => (
          <ItineraryAccordion key={plan.id} plan={plan} />
        ))}
      </div>
    </section>
  )
}

export function DontMiss({ items, cityName }) {
  if (!items?.length) return null
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-700 mb-1">Don&apos;t Miss</h2>
      <p className="text-xs text-gray-400 mb-3">
        Hidden gems and local experiences in {cityName} — beyond the tourist trail.
      </p>
      <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-3 text-sm">
            <span className="text-xl leading-none mt-0.5 flex-shrink-0">{item.emoji}</span>
            <span className="text-gray-700 leading-snug">{item.text}</span>
          </div>
        ))}
      </div>
    </section>
  )
}
